import http from 'apis/config/http-service';
import { useMutationWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { useCustomToast } from 'design-system';
import { UseMutationResult, useQueryClient } from 'react-query';
import {
  EmailProvider,
  notificationProvidersEndpoint,
  NotificationProviderSettings,
  PhoneOtpProvider,
} from './use-get-notification-providers-api';

/** A new choice, or `null` to go back to the server setting. */
export type SaveNotificationProvidersVariables =
  | { section: 'phoneOtp'; providers: PhoneOtpProvider[] | null }
  | { section: 'email'; provider: EmailProvider | null };

type ReturnType = UseMutationResult<NotificationProviderSettings, AxiosError, SaveNotificationProvidersVariables>;

const request = (variables: SaveNotificationProvidersVariables) => {
  if (variables.section === 'phoneOtp') {
    const url = `${notificationProvidersEndpoint}/phone-otp`;
    return variables.providers ? http.put(url, { providers: variables.providers }) : http.delete(url);
  }
  const url = `${notificationProvidersEndpoint}/email`;
  return variables.provider ? http.put(url, { provider: variables.provider }) : http.delete(url);
};

/** Saves or resets one section; the answer is the whole screen's data, put straight into the cache. */
export function useSaveNotificationProvidersApi(): ReturnType {
  const queryClient = useQueryClient();
  const toast = useCustomToast();

  const mutationFn = async (variables: SaveNotificationProvidersVariables) => {
    try {
      const { data } = await request(variables);
      return data.data as NotificationProviderSettings;
    } catch (error: any) {
      toast({
        description: error?.response?.data?.message ?? 'The change could not be saved',
        status: 'error',
      });
      throw error;
    }
  };

  return useMutationWrapper(mutationFn, {
    mutationKey: [`${notificationProvidersEndpoint}/save`],
    onSuccess: (data) => {
      queryClient.setQueryData([notificationProvidersEndpoint], data);
      toast({ description: 'Saved. Every server uses it within a minute.', status: 'success' });
    },
  });
}
